import { cva, type VariantProps } from "class-variance-authority";
import { CircleAlert, CircleCheck, Info, TriangleAlert } from "lucide-react";
import React, { forwardRef } from "react";

import { cn } from "../../handlers/cn.utils";

const alertVariants = cva(
	// A flex row rather than an absolutely placed mark beside hand-tuned padding: the gap then holds
	// at any icon size, and the text wraps against the content edge instead of the alert's. The mark
	// also inherits the variant's own colour — a near-black icon in a red band reads as a stray glyph
	// rather than as part of the warning. `mt-0.5` centres it on the first line of `text-sm`.
	"flex w-full items-start gap-2 rounded-lg border p-4 [&>svg]:mt-0.5 [&>svg]:size-4 [&>svg]:shrink-0 [&>:not(svg)]:min-w-0 [&>:not(svg)]:flex-1",
	{
		variants: {
			variant: {
				default: "bg-primary-100 text-primary-800 border-primary-200",
				destructive: "bg-red-100 text-red-800 border-red-200",
				warning: "bg-yellow-100 text-yellow-800 border-yellow-200",
				info: "bg-blue-100 text-blue-800 border-blue-200",
				success: "bg-green-100 text-green-800 border-green-200",
			},
		},
		defaultVariants: {
			variant: "default",
		},
	},
);

/** Each variant says what it is before it is read, so the mark has to agree with the colour. */
const VARIANT_ICONS = {
	default: Info,
	destructive: CircleAlert,
	warning: TriangleAlert,
	info: Info,
	success: CircleCheck,
} as const;

type AlertProps = React.ComponentPropsWithoutRef<"div"> &
	VariantProps<typeof alertVariants> & {
		/** Replaces the variant's own mark. Pass `null` for an alert that carries none. */
		icon?: React.ReactNode;
	};

const Alert = forwardRef<React.ElementRef<"div">, AlertProps>(
	({ className, variant, icon, children, ...props }, ref) => {
		const VariantIcon = VARIANT_ICONS[variant ?? "default"];

		return (
			<div ref={ref} role="alert" className={cn(alertVariants({ variant }), className)} {...props}>
				{icon === undefined ? <VariantIcon /> : icon}
				{children}
			</div>
		);
	},
);
Alert.displayName = "Alert";

const AlertTitle = forwardRef<React.ElementRef<"h5">, React.ComponentPropsWithoutRef<"h5">>(
	({ className, ...props }, ref) => (
		<h5 ref={ref} className={cn("mb-1 font-medium leading-none tracking-tight", className)} {...props} />
	),
);
AlertTitle.displayName = "AlertTitle";

const AlertDescription = forwardRef<React.ElementRef<"div">, React.ComponentPropsWithoutRef<"div">>(
	({ className, ...props }, ref) => (
		<div ref={ref} className={cn("text-sm [&_p]:leading-relaxed", className)} {...props} />
	),
);
AlertDescription.displayName = "AlertDescription";

export { Alert, AlertDescription, AlertTitle };
